import React, { useState, useMemo, useEffect } from 'react';
import { Input, InputNumber, Select } from 'antd';
import clsx from 'clsx';
import { useFormatData } from 'lib/hook';
import TableTabs from 'component/tableTool/tableTabs';

const EditingCell = (props) => {
    const { field, data, onChangeForm } = props;

    const [value, setValue] = useState();
    useEffect(() => {
        setValue(data?.[field.dataIndex]);
    }, [field, data]);
    const onChangeCallback = (v) => {
        setValue(v);
        onChangeForm?.(field.dataIndex, v);
    };

    switch (field.type) {
        case 'Number': case 'Float': return (
            <InputNumber
                bordered={false}
                controls={false}
                min={0}
                value={value}
                onChange={onChangeCallback}
            />
        );
        case 'Select': return (
            <Select
                bordered={false}
                options={field.options}
                allowClear={field.allowClear !== false}
                showSearch
                filterOption={(input, option) => (
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                )}
                popupClassName="form"
                value={value}
                onChange={onChangeCallback}
            />
        );
        default: return (
            <Input
                bordered={false}
                value={value}
                onChange={(e) => onChangeCallback(e.target.value)}
            />
        );
    }
};

const InfoTable = (props) => {
    const {
        className,
        style,
        centered,
        grid = 3,
        title,
        fields,
        data,
        editable,
        onChangeForm,
        header,
        footer,
        tabBar
    } = props;

    const dataArray = useMemo(() => [data ?? {}], [data]);
    const [formatData] = useFormatData({ columns: fields, data: dataArray });
    const height = useMemo(() => {
        const length = fields?.reduce((prev, curr) => prev + (curr.span || 1), 0);
        let result = Math.ceil(length / grid) * 30 + 32;
        if (header) {
            result += 30;
        }
        if (footer) {
            result += 30;
        }
        return result;
    }, [grid, fields, header, footer]);

    const tabItems = useMemo(() => [
        {
            key: 'infoTable',
            label: title,
            children: (
                <div
                    className="grid text-sm"
                    style={{ gridTemplateColumns: `repeat(${grid}, minmax(0, 1fr))` }}
                >
                    {header && (
                        <div
                            className="h-[30px] border-b border-gray"
                            style={{ gridColumn: `span ${grid} / span ${grid}` }}
                        >
                            {header}
                        </div>
                    )}
                    {fields?.map((field) => (
                        <div
                            key={field.dataIndex}
                            className="flex h-[30px] overflow-hidden"
                            style={{ gridColumn: `span ${field.span || 1}` }}
                        >
                            <div
                                className={clsx(
                                    'px-4 flex items-center font-bold whitespace-nowrap',
                                    'border-b border-r border-gray bg-[#F9F9F9] text-gray-dark02',
                                    centered ? 'justify-center' : 'justify-end'
                                )}
                                style={{ width: `calc(50% / ${(field.span || 1)})` }}
                            >
                                {field.title}
                            </div>
                            <div
                                className={clsx(
                                    'flex items-center whitespace-nowrap',
                                    'border-b border-r border-gray bg-white',
                                    { 'px-4': !editable },
                                    centered ? 'justify-center' : 'justify-start'
                                )}
                                style={{ width: `calc(100% - (50% / ${(field.span || 1)}))` }}
                            >
                                {editable ? (
                                    <EditingCell field={field} data={data} onChangeForm={onChangeForm} />
                                ) : (
                                    <>
                                        {field.render ?
                                            field.render(formatData[field.dataIndex], formatData) :
                                            formatData[field.dataIndex]}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                    {footer && (
                        <div className="h-[30px]" style={{ gridColumn: `span ${grid} / span ${grid}` }}>
                            {footer}
                        </div>
                    )}
                </div>
            )
        }
    ], [centered, grid, title, fields, formatData, editable, data, onChangeForm, header, footer]);

    return (
        <div
            className={clsx(
                'rounded-t-md border-y-2 border-l border-gray relative overflow-hidden',
                className
            )}
            style={{ height, ...style }}
        >
            <div className="absolute top-0 left-0 w-full h-[30px] border-r-2 border-r-gray bg-gray-light01" />
            <TableTabs
                className="relative z-10"
                items={tabItems}
                tabBar={!!tabBar && <div className="px-1">{tabBar}</div>}
            />
        </div>
    );
};

export default InfoTable;
